/**
 * The room's chat: the lines said in it, and which characters are speaking right now.
 *
 * History comes from the API when the page opens; new lines arrive over the live connection, for
 * everybody looking at the room at once. Only a line said while the page is open raises a bubble —
 * opening the room does not replay a week of bubbles over people's heads.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { realtime } from '../lib/realtime'
import type { RoomChatMessage } from '../lib/types'

const KEY = ['room', 'chat'] as const
const KEEP = 100

/** The translation key for a refusal to speak: ours when we know the code, the generic one otherwise. */
export function chatErrorKey(failure: unknown): string {
  const code = failure instanceof ApiError ? failure.code : undefined
  const known = ['too_fast', 'too_long', 'required', 'people_only', 'image_too_big', 'image_too_large', 'not_an_image']
  return code && known.includes(code) ? `room.chat.error.${code}` : 'error.generic'
}

/** Long enough to read: a few seconds, plus a little per character, capped. */
export function bubbleSeconds(text: string): number {
  return Math.min(12, 5 + text.length * 0.06)
}

function merge(existing: RoomChatMessage[] | undefined, message: RoomChatMessage): RoomChatMessage[] {
  const list = existing ?? []
  if (list.some((m) => m.id === message.id)) return list
  return [...list, message].slice(-KEEP)
}

export interface RoomChat {
  messages: RoomChatMessage[]
  /** What each speaking character is saying, by developer id. */
  speech: Map<string, string>
  say: (text: string, image?: string) => Promise<RoomChatMessage>
  sending: boolean
}

export function useRoomChat(): RoomChat {
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: KEY, queryFn: api.room.chat, staleTime: 60_000 })
  const [bubbles, setBubbles] = useState<Map<string, { text: string; until: number }>>(new Map())

  const heard = useCallback(
    (message: RoomChatMessage) => {
      queryClient.setQueryData<RoomChatMessage[]>(KEY, (existing) => merge(existing, message))
      const speaker = message.developerId
      if (!speaker) return
      setBubbles((current) => {
        const next = new Map(current)
        next.set(speaker, { text: message.text, until: Date.now() + bubbleSeconds(message.text) * 1000 })
        return next
      })
    },
    [queryClient],
  )

  useEffect(() => realtime.onRoomMessage(heard), [heard])

  // Bubbles go when their time is up; the timer runs only while somebody is speaking.
  useEffect(() => {
    if (bubbles.size === 0) return
    const soonest = Math.min(...[...bubbles.values()].map((b) => b.until))
    const timer = window.setTimeout(() => {
      const now = Date.now()
      setBubbles((current) => new Map([...current].filter(([, b]) => b.until > now)))
    }, Math.max(50, soonest - Date.now()))
    return () => window.clearTimeout(timer)
  }, [bubbles])

  const send = useMutation({
    mutationFn: ({ text, image }: { text: string; image?: string }) => api.room.say(text, image),
    // The broadcast brings it back too; heard() keeps one copy either way.
    onSuccess: heard,
  })

  // Kept stable between bubbles so the scenes, which are memoised, redraw only when somebody speaks.
  const speech = useMemo(() => new Map([...bubbles].map(([id, b]) => [id, b.text])), [bubbles])

  const say = useCallback(
    (text: string, image?: string) => send.mutateAsync({ text, image }),
    [send],
  )

  return { messages: data ?? [], speech, say, sending: send.isPending }
}
